import { useQuery } from '@tanstack/react-query'

import { supabase } from '@/lib/supabase/cliente'

/** El acta firmada de una orden de trabajo, ya archivada. */
export type ActaFirmada = {
  firmadoEn: string | null
  calificacion: number | null
  observaciones: string | null
  /** Enlace temporal (10 min) para abrir el PDF del bucket privado. */
  url: string | null
}

/**
 * Busca el acta firmada de una orden y le saca un enlace para verla.
 *
 * El PDF vive en el bucket privado 'actas' y el registro de la firma en
 * actas_firma; desde la migración 0104 la oficina puede leer los dos. El enlace
 * se firma corto a propósito: es para abrirlo ahora, no para repartirlo.
 *
 * Se consulta solo cuando la tarjeta está abierta (`activo`): no tiene sentido
 * pedir el acta de veinte servicios que nadie desplegó.
 */
export function useActaFirmada(otNumero: string | null, activo: boolean) {
  const consulta = useQuery({
    queryKey: ['acta-firmada', otNumero],
    enabled: Boolean(otNumero) && activo,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<ActaFirmada | null> => {
      const { data, error } = await supabase
        .from('actas_firma')
        .select('firma_ruta, firmado_en, calificacion, observaciones, ordenes_trabajo!inner(numero)')
        .eq('ordenes_trabajo.numero', otNumero!)
        .not('firma_ruta', 'is', null)
        .order('usado_en', { ascending: false })
        .limit(1)
        .maybeSingle()

      if (error || !data) return null

      const fila = data as unknown as Record<string, unknown>
      const ruta = fila.firma_ruta as string | null
      let url: string | null = null
      if (ruta) {
        const { data: firmada } = await supabase.storage.from('actas').createSignedUrl(ruta, 600)
        url = firmada?.signedUrl ?? null
      }

      return {
        firmadoEn: (fila.firmado_en as string | null) ?? null,
        calificacion: fila.calificacion != null ? Number(fila.calificacion) : null,
        observaciones: (fila.observaciones as string | null) ?? null,
        url,
      }
    },
  })

  return { acta: consulta.data ?? null, cargando: consulta.isLoading }
}
